import { useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { AlertCircle, CheckCircle2, Loader, Send, X, XCircle } from 'lucide-react';
import { useOnClickOutside } from '../../hooks/useOnClickOutside';
import { PLAYER_NAME } from '../../utils/scheduledCommands';
import type { RunSummary } from '../../types/scheduledCommands';
import { cn } from '../../utils/cn';

interface PlayerSendModalProps {
  title: string;
  subtitle?: string;
  /** The player to prefill, usually the viewer's own account. */
  defaultPlayer?: string | null;
  /** What will be sent, rendered for the chosen player (a preview or the exact command). */
  renderPreview?: (player: string) => ReactNode;
  /** Refuse before sending, with a sentence, e.g. when there is no `@a` to narrow. */
  blockedReason?: string | null;
  send: (player: string) => Promise<{ run: RunSummary; responded?: string[] }>;
  onClose: () => void;
  onSent?: () => void;
}

/**
 * Send one message to one player, wherever they are.
 *
 * Quasar routes a `user:` target to the server the player is on, so there is
 * no server to pick. It is also why nothing is sent to someone who is offline,
 * which the result says plainly rather than calling it a failure.
 */
export const PlayerSendModal = ({
  title,
  subtitle,
  defaultPlayer,
  renderPreview,
  blockedReason,
  send,
  onClose,
  onSent,
}: PlayerSendModalProps) => {
  const modalRef = useRef<HTMLDivElement>(null);
  const [player, setPlayer] = useState(defaultPlayer ?? '');
  const [phase, setPhase] = useState<'form' | 'sending' | 'done'>('form');
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<{ run: RunSummary; responded: string[] } | null>(null);

  const close = () => {
    if (phase !== 'sending') onClose();
  };
  useOnClickOutside(modalRef, close);

  const name = player.trim();
  const valid = PLAYER_NAME.test(name);

  const submit = async () => {
    if (!valid || blockedReason) return;
    setPhase('sending');
    setError(null);
    try {
      const sent = await send(name);
      setResult({ run: sent.run, responded: sent.responded ?? [] });
      setPhase('done');
      onSent?.();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'The message could not be sent.');
      setPhase('form');
    }
  };

  const reached = result ? result.responded[0] ?? null : null;
  const delivered = result ? result.run.responded_count > 0 : false;

  return createPortal(
    <div className="modal-overlay z-[9999]">
      <div ref={modalRef} className="modal-panel max-w-lg" role="dialog" aria-modal="true" aria-labelledby="player-send-title">
        <div className="modal-header">
          <div>
            <h2 id="player-send-title" className="modal-title">{title}</h2>
            {subtitle && <p className="modal-subtitle">{subtitle}</p>}
          </div>
          <button type="button" onClick={close} className="modal-close" title="Close" aria-label="Close">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="modal-body space-y-4">
          {phase !== 'done' ? (
            <>
              <div>
                <label htmlFor="player-send-name" className="block text-xs font-medium text-glass-400 mb-1.5">
                  Minecraft name
                </label>
                <input
                  id="player-send-name"
                  className="glass-input w-full font-mono"
                  value={player}
                  onChange={(e) => setPlayer(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') void submit();
                  }}
                  placeholder="Steve"
                  autoComplete="off"
                  spellCheck={false}
                  autoFocus
                  maxLength={16}
                  disabled={phase === 'sending'}
                />
                <p className="text-xs text-glass-500 mt-1">
                  Sent to the server they are on right now. Nobody else sees it, and nothing is sent if they are offline.
                </p>
                {name && !valid && (
                  <p className="text-xs text-red-400 mt-1">A Minecraft name is 3 to 16 letters, numbers or underscores.</p>
                )}
              </div>

              {blockedReason ? (
                <div className="alert alert-warning" role="status">
                  <AlertCircle className="w-4 h-4 shrink-0" />
                  <span>{blockedReason}</span>
                </div>
              ) : (
                renderPreview && valid && <div>{renderPreview(name)}</div>
              )}

              {error && (
                <div className="alert alert-danger" role="alert">
                  <AlertCircle className="w-4 h-4 shrink-0" />
                  <span>{error}</span>
                </div>
              )}
            </>
          ) : (
            result && (
              <div className={cn('alert', delivered ? 'alert-success' : 'alert-warning')} role="status">
                {delivered ? <CheckCircle2 className="w-4 h-4 shrink-0" /> : <XCircle className="w-4 h-4 shrink-0" />}
                <span>
                  {delivered
                    ? `Sent to ${name}${reached ? ` on ${reached}` : ''}.`
                    : `No server answered, so ${name} is probably offline. Nothing was shown to anyone.`}
                </span>
              </div>
            )
          )}
        </div>

        <div className="modal-footer">
          {phase === 'done' ? (
            <>
              <button type="button" onClick={() => { setPhase('form'); setResult(null); }} className="glass-button-secondary">
                Send again
              </button>
              <button type="button" onClick={onClose} className="btn-primary">Done</button>
            </>
          ) : (
            <>
              <button type="button" onClick={close} className="glass-button-secondary" disabled={phase === 'sending'}>
                Cancel
              </button>
              <button
                type="button"
                onClick={() => void submit()}
                className="btn-primary"
                disabled={!valid || !!blockedReason || phase === 'sending'}
              >
                {phase === 'sending' ? <Loader className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                {phase === 'sending' ? 'Sending…' : `Send to ${valid ? name : 'player'}`}
              </button>
            </>
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
};
